import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import CircleIcon from "@mui/icons-material/Circle";
import { Skeleton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useNotification } from "../../context/NotificationContext";

export default function NotificationBell({ collapsed = false }) {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAllRead,
    openNotification,
    toRelativeTime,
  } = useNotification();

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);
  const visibleNotifications = notifications.slice(0, 8);

  useEffect(() => {
    function handleClickOutside(event) {
      const inTrigger = rootRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);
      if (!inTrigger && !inDropdown) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications({ page: 1, perPage: 20, unreadOnly: false, silent: true });
    }
  }, [open, fetchNotifications]);

  const notificationTip =
    unreadCount > 0
      ? `Notifications · ${unreadCount} unread`
      : "Notifications";
  const inactiveBg = "rgba(234,179,8,0.12)";
  const inactiveHoverBg = "rgba(234,179,8,0.2)";
  const activeBg = "#eab308";
  const activeText = "#ffffff";
  const inactiveText = "#eab308";

  return (
    <div ref={rootRef} style={{ position: "relative", width: "100%", minWidth: 0 }}>
      <Tooltip title={notificationTip} arrow placement="right">
        <span
          style={{
            display: "flex",
            width: "100%",
            minWidth: 0,
            flex: "1 1 0",
            justifyContent: "center",
          }}
        >
          <button
            type="button"
            className="notification-bell-trigger"
            onClick={() => setOpen((prev) => !prev)}
            aria-label="Open notifications"
            aria-expanded={open}
            aria-pressed={open}
            style={{
              width: collapsed ? 38 : "100%",
              minWidth: 0,
              maxWidth: "100%",
              minHeight: collapsed ? 38 : 48,
              height: collapsed ? 38 : "auto",
              borderRadius: 10,
              border: open ? "1px solid #eab308" : "1px solid rgba(234,179,8,0.45)",
              background: open ? activeBg : inactiveBg,
              color: open ? activeText : inactiveText,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0,
              padding: collapsed ? 0 : "6px 4px 5px",
              cursor: "pointer",
              position: "relative",
              transition: "all 0.18s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = open ? activeBg : inactiveHoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = open ? activeBg : inactiveBg;
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  color: open ? "#ffffff" : "#eab308",
                }}
              >
                <NotificationsRoundedIcon
                  fontSize="small"
                  htmlColor={open ? "#ffffff" : "#eab308"}
                  sx={
                    open
                      ? {
                          color: "#ffffff !important",
                          fill: "#ffffff !important",
                          "& path": { fill: "#ffffff !important" },
                        }
                      : {
                          color: "#eab308 !important",
                          fill: "#eab308 !important",
                          "& path": { fill: "#eab308 !important" },
                        }
                  }
                />

                {unreadCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -5,
                      right: -7,
                      minWidth: 18,
                      height: 18,
                      borderRadius: 999,
                      background: "#ef4444",
                      color: "#fff",
                      fontSize: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 5px",
                      fontWeight: 700,
                      border: "2px solid #1e293b",
                      lineHeight: 1,
                    }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              {!collapsed && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: open ? "#ffffff" : "#eab308",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Notifications
                </span>
              )}
            </div>
          </button>
        </span>
      </Tooltip>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: 16,
              right: 16,
              width: 360,
              maxWidth: "min(360px, calc(100vw - 32px))",
              background: "var(--bg-card, #ffffff)",
              borderRadius: 12,
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
              border: "1px solid var(--border-color, #e2e8f0)",
              overflow: "hidden",
              zIndex: 10000,
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid var(--border-color, #e2e8f0)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "var(--table-header, #f8fafc)",
              }}
            >
              <strong style={{ color: "var(--text-primary, #0f172a)", fontSize: 14 }}>
                Notifications
              </strong>
              <button
                type="button"
                onClick={markAllRead}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--accent)",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                }}
              >
                <DoneAllRoundedIcon sx={{ fontSize: 15 }} />
                Mark all read
              </button>
            </div>

            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {loading ? (
                <div style={{ padding: 12 }}>
                  <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1, mb: 1 }} />
                  <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1, mb: 1 }} />
                  <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
                </div>
              ) : visibleNotifications.length === 0 ? (
                <p
                  style={{
                    margin: 0,
                    padding: 14,
                    fontSize: 13,
                    color: "var(--text-secondary, #64748b)",
                  }}
                >
                  No notifications yet.
                </p>
              ) : (
                visibleNotifications.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      openNotification(item);
                      setOpen(false);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      borderBottom: "1px solid #f1f5f9",
                      background: item.is_read ? "#fff" : "#eff6ff",
                      padding: "10px 12px",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {!item.is_read ? (
                        <CircleIcon sx={{ fontSize: 8, color: "var(--accent)" }} />
                      ) : (
                        <span style={{ width: 8 }} />
                      )}
                      <strong style={{ fontSize: 13, color: "#0f172a" }}>{item.title}</strong>
                    </div>
                    <p style={{ margin: "6px 0 0 16px", fontSize: 12, color: "#475569" }}>
                      {item.message}
                    </p>
                    <span style={{ marginLeft: 16, fontSize: 11, color: "#94a3b8" }}>
                      {toRelativeTime(item.created_at)}
                    </span>
                  </button>
                ))
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid var(--border-color, #e2e8f0)",
                background: "var(--table-header, #f8fafc)",
                color: "var(--accent, #1d4ed8)",
                fontSize: 13,
                fontWeight: 600,
                padding: "10px 12px",
                cursor: "pointer",
              }}
            >
              View all notifications
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}
